import { ChartPivotRow, Filter } from '@cubejs-client/core';
import { UseCubeQueryResult } from '@cubejs-client/react';
import { format } from 'date-fns';
import { ChartColors } from 'ui/analytics/consts';
import { isDefined, isEmptyString } from 'lib/typeguards';
import { DateRangePickerValue } from 'ui/MuiDateRangePicker';

export enum EAudienceMeasureView {
    ACTIVE_USERS_VIEW = 'active_user_order_metrics',
    AUDIENCE_VIEW = 'segment_order_metrics'
}

export enum EAudienceMeasureType {
    ORDER_COUNT = 'order_count',
    AVG_VALUE = 'avg_total_to_pay',
    FREQUENCY = 'frequency',
    TOTAL_VALUE = 'sum_total_to_pay'
}

export const AudienceMeasureKeys = {
    ACTIVE_USERS_ORDER_COUNT: `${EAudienceMeasureView.ACTIVE_USERS_VIEW}.${EAudienceMeasureType.ORDER_COUNT}`,
    ACTIVE_USERS_AVG_VALUE: `${EAudienceMeasureView.ACTIVE_USERS_VIEW}.${EAudienceMeasureType.AVG_VALUE}`,
    ACTIVE_USERS_FREQUENCY: `${EAudienceMeasureView.ACTIVE_USERS_VIEW}.${EAudienceMeasureType.FREQUENCY}`,
    ACTIVE_USERS_TOTAL_VALUE: `${EAudienceMeasureView.ACTIVE_USERS_VIEW}.${EAudienceMeasureType.TOTAL_VALUE}`,
    AUDIENCE_ORDER_COUNT: `${EAudienceMeasureView.AUDIENCE_VIEW}.${EAudienceMeasureType.ORDER_COUNT}`,
    AUDIENCE_AVG_VALUE: `${EAudienceMeasureView.AUDIENCE_VIEW}.${EAudienceMeasureType.AVG_VALUE}`,
    AUDIENCE_FREQUENCY: `${EAudienceMeasureView.AUDIENCE_VIEW}.${EAudienceMeasureType.FREQUENCY}`,
    AUDIENCE_TOTAL_VALUE: `${EAudienceMeasureView.AUDIENCE_VIEW}.${EAudienceMeasureType.TOTAL_VALUE}`
};

export function parseAudienceMeasureKey(
    key: string
): [EAudienceMeasureView | undefined, EAudienceMeasureType | undefined] {
    const [view, measure] = key.split('.');
    if (!view || !measure) {
        return [undefined, undefined];
    }
    if (!Object.values(EAudienceMeasureView).includes(view as EAudienceMeasureView)) {
        return [undefined, undefined];
    }
    if (!Object.values(EAudienceMeasureType).includes(measure as EAudienceMeasureType)) {
        return [undefined, undefined];
    }
    return [view, measure] as [EAudienceMeasureView, EAudienceMeasureType];
}

export function getAudienceMeasureKey(
    view: EAudienceMeasureView,
    measure: EAudienceMeasureType
): keyof typeof AudienceMeasureKeys {
    return `${view}.${measure}` as keyof typeof AudienceMeasureKeys;
}

export enum EAudienceTimeGranularity {
    DAY = 'day',
    WEEK = 'week',
    MONTH = 'month'
}

export function isAudienceAnalyticsMeasure(value: string): value is keyof typeof AudienceMeasureKeys {
    return Object.values(AudienceMeasureKeys).includes(value);
}

export function isAudienceAnalyticsMeasureType(value: string): value is EAudienceMeasureType {
    return Object.values(EAudienceMeasureType).includes(value as EAudienceMeasureType);
}

export interface AudienceMeasureTotalsRecord {
    value: number;
    loading: boolean;
}

export interface AudienceMeasureTotal {
    key: EAudienceMeasureType;
    current: AudienceMeasureTotalsRecord;
    retro: AudienceMeasureTotalsRecord;
    difference?: number;
}

export function formatAnalyticsMeasureValue(
    value: number,
    format: 'number' | 'currency' = 'number',
    getCurrencyString: (value: number) => string
) {
    if (format === 'currency') {
        return getCurrencyString(value ?? 0);
    }
    return new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 2 }).format(value ?? 0);
}

function getCubeAudienceAnalyticsAudienceFilter(view: EAudienceMeasureView, audiences: string[]): Filter {
    if (!Array.isArray(audiences) || audiences.length === 0) {
        return undefined;
    }
    return {
        member: `${view}.segment_id`,
        operator: 'equals',
        values: audiences
    };
}
function getCubeAudienceAnalyticsLocationFilter(view: EAudienceMeasureView, locationId?: string): Filter {
    if (!isDefined(locationId) || isEmptyString(locationId)) {
        return undefined;
    }
    return {
        member: `${view}.location_id`,
        operator: 'equals',
        values: [locationId]
    };
}

function getCubeActiveUsersFilter(): Filter {
    return undefined;
}

export function getCubeAudienceAnalyticsFilters(locationId?: string, audienceIds?: string[]): Filter[] {
    const filters: Filter[] = [];
    const locationFilter = getCubeAudienceAnalyticsLocationFilter(
        EAudienceMeasureView.AUDIENCE_VIEW,
        locationId
    );
    if (locationFilter) {
        filters.push(locationFilter);
    }
    const audienceFilter = getCubeAudienceAnalyticsAudienceFilter(
        EAudienceMeasureView.AUDIENCE_VIEW,
        audienceIds
    );
    if (audienceFilter) {
        filters.push(audienceFilter);
    }
    return filters;
}

export function getCubeActiveUsersAnalyticsFilters(locationId?: string): Filter[] {
    const filters: Filter[] = [];
    const locationFilter = getCubeAudienceAnalyticsLocationFilter(
        EAudienceMeasureView.ACTIVE_USERS_VIEW,
        locationId
    );
    if (locationFilter) {
        filters.push(locationFilter);
    }
    const activeUsersFilterFilter = getCubeActiveUsersFilter();
    if (activeUsersFilterFilter) {
        filters.push(activeUsersFilterFilter);
    }
    return filters;
}

// TODO: replace with better colours
export const AUDIENCE_ANALYTICS_COLORS: [string, string, string, string, string] = [
    ChartColors.ORANGE,
    ChartColors.RED,
    ChartColors.BLUE,
    ChartColors.TEAL,
    ChartColors.YELLOW
];

export interface AudienceMeasureConfig {
    title: string;
    subtitle: string;
    format: 'number' | 'currency';
    description: string;
}

export const AUDIENCE_MEASURE_CONFIG: Record<EAudienceMeasureType, AudienceMeasureConfig> = {
    [EAudienceMeasureType.ORDER_COUNT]: {
        title: 'Total Orders',
        subtitle: 'across all locations',
        format: 'number',
        description: 'Total order count across all locations within the time period'
    },
    [EAudienceMeasureType.AVG_VALUE]: {
        title: 'ATV',
        subtitle: 'per order',
        format: 'currency',
        description: 'Average spend per order within the time period'
    },
    [EAudienceMeasureType.FREQUENCY]: {
        title: 'Frequency',
        subtitle: 'visits per active user',
        format: 'number',
        description: 'Average number of orders per user within the time period'
    },
    [EAudienceMeasureType.TOTAL_VALUE]: {
        title: 'Total Order Value',
        subtitle: 'across all orders',
        format: 'currency',
        description: 'Total spend across all orders within the time period'
    }
};

export const AUDIENCE_MEASURE_CONFIG_VIEW_AUDIENCE: Record<EAudienceMeasureType, AudienceMeasureConfig> = {
    [EAudienceMeasureType.ORDER_COUNT]: {
        ...AUDIENCE_MEASURE_CONFIG[EAudienceMeasureType.ORDER_COUNT],
        subtitle: 'across all locations, last 3 months'
    },
    [EAudienceMeasureType.AVG_VALUE]: {
        ...AUDIENCE_MEASURE_CONFIG[EAudienceMeasureType.AVG_VALUE],
        subtitle: 'per order, last 3 months'
    },
    [EAudienceMeasureType.FREQUENCY]: {
        ...AUDIENCE_MEASURE_CONFIG[EAudienceMeasureType.FREQUENCY],
        subtitle: 'avg visits, last 3 months'
    },
    [EAudienceMeasureType.TOTAL_VALUE]: {
        ...AUDIENCE_MEASURE_CONFIG[EAudienceMeasureType.TOTAL_VALUE],
        subtitle: 'spent, last 3 months'
    }
};

export function formatAudienceAnalyticsChartDate(row: ChartPivotRow) {
    return format(new Date(row.x), 'dd/MM/yyyy');
}

export const ACTIVE_USERS_TOTALS_AUDIENCE_ANALYTICS_MEASURES = [
    AudienceMeasureKeys.ACTIVE_USERS_ORDER_COUNT,
    AudienceMeasureKeys.ACTIVE_USERS_AVG_VALUE,
    AudienceMeasureKeys.ACTIVE_USERS_TOTAL_VALUE
];

export const ACTIVE_USERS_FREQUENCY_AUDIENCE_ANALYTICS_MEASURES = [
    AudienceMeasureKeys.ACTIVE_USERS_FREQUENCY
];

export const AUDIENCE_TOTALS_ANALYTICS_MEASURES = [
    AudienceMeasureKeys.AUDIENCE_ORDER_COUNT,
    AudienceMeasureKeys.AUDIENCE_AVG_VALUE,
    AudienceMeasureKeys.AUDIENCE_TOTAL_VALUE
];

export const AUDIENCE_FREQUENCY_ANALYTICS_MEASURES = [AudienceMeasureKeys.AUDIENCE_FREQUENCY];

function getAudienceMeasureRecordsWithRetroData(
    activeDataQueries: UseCubeQueryResult<any, any>[],
    retroDataQueries: UseCubeQueryResult<any, any>[]
) {
    const retroDataRecord: Partial<Record<EAudienceMeasureType, AudienceMeasureTotalsRecord>> = {};
    retroDataQueries.forEach(query => {
        query.resultSet?.series().forEach(item => {
            const [_, measure] = parseAudienceMeasureKey(item.key);
            const itemData = item.series.at(-1);
            retroDataRecord[measure] = {
                value: itemData?.value || 0,
                loading: query.isLoading
            };
        });
    });
    const currentDataRecord: Partial<Record<EAudienceMeasureType, AudienceMeasureTotalsRecord>> = {};
    activeDataQueries.forEach(query => {
        query.resultSet?.series().forEach(item => {
            const [_, measure] = parseAudienceMeasureKey(item.key);
            const itemData = item.series.at(-1);
            currentDataRecord[measure] = {
                value: itemData?.value || 0,
                loading: query.isLoading
            };
        });
    });
    return [currentDataRecord, retroDataRecord];
}

export function getAudienceMeasuresListWithRetroData(
    activeDataQueries: UseCubeQueryResult<any, any>[],
    retroDataQueries: UseCubeQueryResult<any, any>[],
    includeRetroData?: boolean
): [
    AudienceMeasureTotal[],
    Partial<Record<string, AudienceMeasureTotalsRecord>>,
    Partial<Record<string, AudienceMeasureTotalsRecord>>
] {
    const [currentMeasureValues, retroMeasureValues] = getAudienceMeasureRecordsWithRetroData(
        activeDataQueries,
        retroDataQueries
    );
    const measuresList = [
        EAudienceMeasureType.ORDER_COUNT,
        EAudienceMeasureType.AVG_VALUE,
        EAudienceMeasureType.FREQUENCY,
        EAudienceMeasureType.TOTAL_VALUE
    ];
    if (includeRetroData) {
        return [
            measuresList.map<AudienceMeasureTotal>(item => ({
                key: item,
                current: currentMeasureValues[item],
                retro: retroMeasureValues[item],
                difference:
                    !retroMeasureValues[item]?.value || !currentMeasureValues[item]?.value
                        ? undefined
                        : ((currentMeasureValues[item]?.value || 0) -
                              (retroMeasureValues[item]?.value || 0)) /
                          (retroMeasureValues[item]?.value || 0)
            })),
            currentMeasureValues,
            retroMeasureValues
        ];
    }
    return [
        measuresList.map<AudienceMeasureTotal>(item => ({
            key: item,
            current: currentMeasureValues[item],
            retro: {
                loading: false,
                value: 0
            },
            difference: undefined
        })),
        currentMeasureValues,
        {}
    ];
}

function getAudienceAnalyticsCreatedAtDimension(view: EAudienceMeasureView): string {
    return `${view}.created_at`;
}

export function getAudienceAnalyticsTimeDimensions(
    view: EAudienceMeasureView,
    dateRange: DateRangePickerValue,
    granularity?: string
) {
    return [
        {
            dimension: getAudienceAnalyticsCreatedAtDimension(view),
            dateRange: [
                format(dateRange.startDate, 'yyyy-MM-dd'),
                format(dateRange.endDate, 'yyyy-MM-dd')
            ] as [string, string],
            granularity
        }
    ];
}
export function getAudienceAnalyticsSegmentDimension(view: EAudienceMeasureView): string {
    return `${view}.segment_id`;
}
